import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  GlobalPut,
  RbdMaxReceived,
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App } from '@ps20-frontend/frontend-models';
import { ToolbarActions } from '../toolbar.actions';
import { RBD } from '../interfaces/rbd';
import { IAppState } from '../toolbarState';
import { getIndexForToolbarElement } from '../interfaces/getIndexByAppOrder';

export function* watchRbdMaxReceived() {
  yield takeEvery(GlobalActions.RBD_MAX_RECEIVED, safeSaga(rbdMaxSaga, App.SHOP_TOOLBAR));
}

export function* rbdMaxSaga(action: GlobalAction<RbdMaxReceived>) {
  const state: IAppState = yield select();
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const globalPut = GlobalPut.getInstance();

  yield call(globalPut.put, new GlobalActions().requestAngeboteInWarenkorb());
  const angeboteInWarenkorbAction: GlobalAction<Array<AngebotsId>> = yield take(
    GlobalActions.ANGEBOTE_IN_WARENKORB_PROVIDED,
  );
  const angeboteInWarenkorb = angeboteInWarenkorbAction.payload;

  let newRbdArray: Array<RBD> = cloneDeep(state.rbd);
  if (shouldShowRBDWasResetedInfo(newRbdArray, action.payload, action.payload.rbdMax)) {
    globalEventDispatcher.dispatch(new GlobalActions().rabattHinweisChanged(true));
  }

  newRbdArray = getNewRbdArray(action.payload, newRbdArray).sort((a, b) => {
    return (
      getIndexForToolbarElement(a, angeboteInWarenkorb) -
      getIndexForToolbarElement(b, angeboteInWarenkorb)
    );
  });

  yield put(new ToolbarActions().rbdMaxUpdated(newRbdArray));
}

function getNewRbdArray(rbdMaxReceivedPayload: RbdMaxReceived, newRbdArray: Array<RBD>) {
  if (rbdMaxReceivedPayload.rbdMax === 0) {
    newRbdArray = deleteElementFromRBDArray(newRbdArray, rbdMaxReceivedPayload);
  } else {
    newRbdArray = updateNewRbdArrayAndRbdPopupOpen(newRbdArray, rbdMaxReceivedPayload);
  }
  return newRbdArray;
}

function updateNewRbdArrayAndRbdPopupOpen(
  newRbdArray: Array<RBD>,
  rbdMaxReceivedPayload: RbdMaxReceived,
): Array<RBD> {
  if (hasElementToUpdate(newRbdArray, rbdMaxReceivedPayload)) {
    newRbdArray = updateElementInNewRBDArray(newRbdArray, rbdMaxReceivedPayload);
  } else {
    newRbdArray.push(createNewRbdElement(rbdMaxReceivedPayload));
  }
  return newRbdArray;
}

function updateElementInNewRBDArray(
  newRbdArray: Array<RBD>,
  rbdMaxReceivedPayload: RbdMaxReceived,
): Array<RBD> {
  return newRbdArray.map(rbdElem => {
    if (
      rbdElem.appId === rbdMaxReceivedPayload.appId &&
      rbdElem.angebotsId === rbdMaxReceivedPayload.angebotsId
    ) {
      return createNewRbdElement(rbdMaxReceivedPayload);
    }
    return rbdElem;
  });
}

function shouldShowRBDWasResetedInfo(
  newRbdArray: Array<RBD>,
  angebotOfElementToUpdate: AngebotsId,
  newRbdMax: number,
) {
  return (
    hasElementToUpdate(newRbdArray, angebotOfElementToUpdate) &&
    findElementToUpdate(newRbdArray, angebotOfElementToUpdate).rbd_aktuell !== 0 &&
    newRbdMax !== 0
  );
}

function hasElementToUpdate(
  rbdElements: Array<RBD>,
  angebotOfElementToUpdate: AngebotsId,
): boolean {
  return findElementToUpdate(rbdElements, angebotOfElementToUpdate) != null;
}

function findElementToUpdate(rbdElements: Array<RBD>, { appId, angebotsId }: AngebotsId): RBD {
  return rbdElements.find(elem => {
    return elem.appId === appId && elem.angebotsId === angebotsId;
  });
}

function deleteElementFromRBDArray(
  rbdElements: Array<RBD>,
  angebotToRemove: AngebotsId,
): Array<RBD> {
  return rbdElements.filter(elem => {
    return elem.appId !== angebotToRemove.appId || elem.angebotsId !== angebotToRemove.angebotsId;
  });
}

function createNewRbdElement(payload: RbdMaxReceived): RBD {
  return {
    appId: payload.appId,
    angebotsId: payload.angebotsId,
    rbd_max: payload.rbdMax,
    rbd_aktuell: 0,
    rbd_changed: 0,
    info: payload.info,
    versicherung: payload.versicherung,
  };
}
